// Storage de access token. Em memória + fallback em sessionStorage para sobreviver
// a hot reloads em dev (refresh token verdadeiro vive em cookie httpOnly — readme §16).
//
// `SESSION_FLAG` em localStorage marca "este browser já teve sessão" — usado pelo
// AuthBootstrap pra decidir se vale tentar refresh em cold start. Visitante novo
// (sem flag) pula o refresh pra não ruidar 401 no console da página de login.
// Por que localStorage e não sessionStorage: o cookie httpOnly persiste por dias;
// queremos retomar a sessão mesmo após fechar a aba.
//
// ## Por que existem DUAS formas de limpar
//
// Antes havia só `setToken(null)`, que apagava o token E a flag. Todo caminho de erro
// chamava isso — inclusive falha de rede e 5xx. Como a flag é o que autoriza o
// AuthBootstrap a tentar retomar a sessão pelo cookie, apagá-la tornava o logout
// PERMANENTE: o carregamento seguinte nem tentava, e o usuário tinha que digitar a
// senha de novo mesmo com o cookie de refresh válido por 30 dias.
//
//   - `clearAccessToken()` — o token atual não serve (expirou, o backend piscou).
//     A sessão continua existindo; o cookie ainda pode renová-la. É o default.
//   - `clearSession()`     — a sessão acabou DE VERDADE (logout explícito, ou o
//     servidor recusou o refresh com um código definitivo). Só aqui a flag sai.
//
// Na dúvida, use `clearAccessToken()`: o custo de errar para esse lado é uma
// requisição extra de refresh; para o outro lado, é expulsar o usuário.

const STORAGE_KEY = 'chatia:access-token';
const SESSION_FLAG = 'chatia:has-session';

let inMemoryToken: string | null = null;

export function getToken(): string | null {
  if (inMemoryToken) return inMemoryToken;
  if (typeof sessionStorage === 'undefined') return null;
  inMemoryToken = sessionStorage.getItem(STORAGE_KEY);
  return inMemoryToken;
}

/** Persiste o access token e marca que este browser tem uma sessão viva. */
export function setToken(token: string): void {
  inMemoryToken = token;
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(STORAGE_KEY, token);
  if (typeof localStorage !== 'undefined') localStorage.setItem(SESSION_FLAG, '1');
}

/**
 * Descarta só o access token. PRESERVA a flag de sessão — o cookie httpOnly de
 * refresh continua valendo e o próximo boot deve tentar retomar a sessão.
 */
export function clearAccessToken(): void {
  inMemoryToken = null;
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Encerra a sessão neste browser: apaga o token e a flag. Use APENAS quando o
 * servidor recusou definitivamente (ou o usuário deslogou) — nunca em erro de rede.
 */
export function clearSession(): void {
  clearAccessToken();
  if (typeof localStorage !== 'undefined') localStorage.removeItem(SESSION_FLAG);
}

export function hasPriorSession(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(SESSION_FLAG) === '1';
}

/**
 * `exp` do access token (epoch em segundos), ou null se não der para ler.
 *
 * Lê do próprio JWT em vez de guardar o `accessTokenExp` da resposta porque o token
 * também vem do sessionStorage num cold start, onde não há resposta nenhuma — assim
 * o agendamento de refresh funciona igual nos dois caminhos. É só a metade `payload`,
 * sem verificar assinatura: quem valida é o servidor; aqui só se decide QUANDO renovar.
 */
export function readTokenExp(token: string): number | null {
  const payloadB64 = token.split('.')[1];
  if (!payloadB64) return null;
  try {
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(json) as { exp?: unknown };
    return typeof parsed.exp === 'number' ? parsed.exp : null;
  } catch {
    return null;
  }
}
